import * as React from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import { validateInput } from '../../util/validateFormInput';
import {
  fetchFormDataStart,
  submitFormRequest
} from '../../../redux/slices/subSellerSignUpSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AlertPopUp from './AlertPopUp';
import SuccessPopUp from '../../../components/SellerProfile/SuccessPopUp/SuccessPopUp';
import CustomDropdown from '../../../components/CustomDropdown/CustomDropdown';

const SignUp = () => {
  const theme = useTheme();
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const announcementRef = React.useRef<HTMLDivElement>(null);
  const errorAnnounceRef = React.useRef<HTMLDivElement>(null);

  const isMobile = useMediaQuery(theme.breakpoints.between('xs', 'md'));
  const isMobileTablet = useMediaQuery(
    '(min-width:0px) and (max-width:1023px)'
  );

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  const { businessModelOptions, GDSOptions, formSubmitted } = useSelector(
    (state: any) => state.subSellerSignUp
  );
  const { showSuccessPopUp } = useSelector((state: any) => state.sellerProfile);

  const [formData, setFormData] = React.useState({
    seller_aggregator_id: '',
    travel_agency_name: '',
    iata_number: '',
    agency_address: '',
    agency_phone_number: '',
    agency_email: '',
    agency_website: '',
    sub_seller_business_type_id: '',
    contact_first_name: '',
    contact_last_name: ''
  });

  const [formValidation, setFormValidation] = React.useState({
    seller_aggregator_id: false,
    travel_agency_name: false,
    iata_number: false,
    agency_address: false,
    agency_phone_number: false,
    agency_email: false,
    agency_website: false,
    sub_seller_business_type_id: false,
    contact_first_name: false,
    contact_last_name: false
  });

  const [formError, setFormError] = React.useState({
    seller_aggregator_id: false,
    travel_agency_name: false,
    iata_number: false,
    agency_address: false,
    agency_phone_number: false,
    agency_email: false,
    agency_website: false,
    sub_seller_business_type_id: false,
    contact_first_name: false,
    contact_last_name: false
  });

  React.useEffect(() => {
    dispatch(fetchFormDataStart());
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  }, [dispatch]);

  const [question1Answer, setQuestion1Answer] = React.useState('');
  const [question2Answer, setQuestion2Answer] = React.useState('');
  const [showAlertPopup, setShowAlertPopup] = React.useState(false);
  const [announceError, setAnnounceError] = React.useState('');

  const showForm = question1Answer === 'yes' && question2Answer === 'yes';

  const sub_seller_business_type_id = 'SSBT001';

  const isIataRequired =
    formData.sub_seller_business_type_id === sub_seller_business_type_id;

  const handleFormData = (e: any) => {
    setFormData((prevData) => ({
      ...prevData,
      [e.target.name]: e.target.value
    }));

    const isBusinessTypeChange =
      e.target.name === 'sub_seller_business_type_id';
    const newBusinessType = isBusinessTypeChange
      ? e.target.value
      : formData.sub_seller_business_type_id;
    const willIataBeRequired = newBusinessType === sub_seller_business_type_id;

    // Validate current field
    let op = e.target.value.trim()
      ? validateInput(
          {
            content: e.target.value,
            validate: e.target.id
          },
          t
        )
      : { error: true, msg: '' };

    // Special handling for IATA field - only validate if required
    if (e.target.name === 'iata_number') {
      if (!willIataBeRequired && !e.target.value.trim()) {
        op = { error: false, msg: '' };
      }
    }

    setFormError((prevData: any) => ({
      ...prevData,
      [e.target.name]: op.msg
    }));
    setFormValidation((prevData) => ({
      ...prevData,
      [e.target.name]: !op.error
    }));

    // If business type changes, re-validate IATA field
    if (isBusinessTypeChange) {
      if (willIataBeRequired && !formData.iata_number.trim()) {
        setFormError((prevData: any) => ({
          ...prevData,
          iata_number: false
          // t('sellerProfile.dashboard.validation.iata')
        }));
        setFormValidation((prevData) => ({
          ...prevData,
          iata_number: false
        }));
      } else if (!willIataBeRequired && !formData.iata_number) {
        // Clear IATA error if it's no longer required
        setFormError((prevData: any) => ({
          ...prevData,
          iata_number: false
        }));
        setFormValidation((prevData) => ({
          ...prevData,
          iata_number: true
        }));
      } else if (willIataBeRequired && formData.iata_number.trim()) {
        // Re-validate IATA if it has a value and is now required
        const iataOp = validateInput(
          {
            content: formData.iata_number,
            validate: 'iata'
          },
          t
        );
        setFormError((prevData: any) => ({
          ...prevData,
          iata_number: iataOp.msg
        }));
        setFormValidation((prevData) => ({
          ...prevData,
          iata_number: !iataOp.error
        }));
      }
    }
  };

  const handleSubmit = async () => {
    const fieldOrder: string[] = [
      'seller_aggregator_id',
      'travel_agency_name',
      'sub_seller_business_type_id',
      'iata_number',
      'agency_address',
      'agency_phone_number',
      'contact_first_name',
      'contact_last_name',
      'agency_email',
      'agency_website'
    ];

    const fieldLabelMap: Record<string, string> = {
      seller_aggregator_id: t('subSellerOnboarding.form.field1.data'),
      travel_agency_name: t('subSellerOnboarding.form.field2.data'),
      sub_seller_business_type_id: t('subSellerOnboarding.form.field9.data'),
      iata_number: t('subSellerOnboarding.form.field3.data'),
      agency_address: t('subSellerOnboarding.form.field4.data'),
      agency_phone_number: t('subSellerOnboarding.form.field5.data'),
      contact_first_name: t('subSellerOnboarding.form.field10.data'),
      contact_last_name: t('subSellerOnboarding.form.field11.data'),
      agency_email: t('subSellerOnboarding.form.field6.data'),
      agency_website: t('subSellerOnboarding.form.field7.data')
    };

    const newFormError: any = {};
    const newFormValidation: any = {};
    let hasError = false;
    const missingFields: string[] = [];
    const errorMessages: string[] = [];
    console.log(isIataRequired);
    fieldOrder.forEach((key) => {
      // IATA is only required for specific business type
      if (key === 'iata_number') {
        if (!isIataRequired && !formData[key].trim()) {
          newFormError[key] = false;
          newFormValidation[key] = true;
          return;
        }

        if (!formData[key].trim()) {
          const msg = t('subSellerOnboarding.form.helperText');
          newFormError[key] = msg;
          newFormValidation[key] = false;
          hasError = true;
          if (fieldLabelMap[key]) {
            missingFields.push(fieldLabelMap[key]);
            errorMessages.push(`${fieldLabelMap[key]}: ${msg}`);
          }
          console.log(newFormError[key]);
          return;
        }

        const op = validateInput(
          {
            content: formData[key],
            validate: 'iata'
          },
          t
        );
        newFormError[key] = op.error ? op.msg : false;
        newFormValidation[key] = !op.error;
        if (op.error) {
          hasError = true;
          if (
            fieldLabelMap[key] &&
            !missingFields.includes(fieldLabelMap[key])
          ) {
            missingFields.push(fieldLabelMap[key]);
          }
          if (op.msg) errorMessages.push(`${fieldLabelMap[key]}: ${op.msg}`);
        }
        return;
      }

      // Phone, email, website are always required
      if (
        ['agency_phone_number', 'agency_email', 'agency_website'].includes(key)
      ) {
        if (!formData[key].trim()) {
          const msg = t('subSellerOnboarding.form.helperText');
          newFormError[key] = msg;
          newFormValidation[key] = false;
          hasError = true;
          if (fieldLabelMap[key]) {
            missingFields.push(fieldLabelMap[key]);
            errorMessages.push(`${fieldLabelMap[key]}: ${msg}`);
          }
        } else {
          const validateType =
            key === 'agency_phone_number'
              ? 'phone'
              : key === 'agency_email'
              ? 'email'
              : key === 'agency_website'
              ? 'url'
              : key;
          const op = validateInput(
            {
              content: formData[key],
              validate: validateType
            },
            t
          );
          newFormError[key] = op.error ? op.msg : false;
          newFormValidation[key] = !op.error;
          if (op.error) {
            hasError = true;
            if (
              fieldLabelMap[key] &&
              !missingFields.includes(fieldLabelMap[key])
            ) {
              missingFields.push(fieldLabelMap[key]);
            }
            if (op.msg) errorMessages.push(`${fieldLabelMap[key]}: ${op.msg}`);
          }
        }
        return;
      }

      // Other fields
      if (!formData[key].trim()) {
        const msg = t('subSellerOnboarding.form.helperText');
        newFormError[key] = msg;
        newFormValidation[key] = false;
        hasError = true;
        if (fieldLabelMap[key]) {
          missingFields.push(fieldLabelMap[key]);
          errorMessages.push(`${fieldLabelMap[key]}: ${msg}`);
        }
      } else {
        const op = validateInput(
          {
            content: formData[key],
            validate: key
          },
          t
        );
        newFormError[key] = op.error ? op.msg : false;
        newFormValidation[key] = !op.error;
        if (op.error) {
          hasError = true;
          if (
            fieldLabelMap[key] &&
            !missingFields.includes(fieldLabelMap[key])
          ) {
            missingFields.push(fieldLabelMap[key]);
          }
          if (op.msg) errorMessages.push(`${fieldLabelMap[key]}: ${op.msg}`);
        }
      }
    });

    setFormError(newFormError);
    setFormValidation(newFormValidation);

    if (hasError) {
      setAnnounceError('');
      setTimeout(() => {
        setAnnounceError(
          [
            t('subSellerOnboarding.form.submitErrorMessage'),
            ...errorMessages
          ].join(', ')
        );
      }, 100);

      setTimeout(() => {
        setAnnounceError('');
      }, 500);
    } else {
      setAnnounceError('');
      dispatch(
        submitFormRequest({
          formData,
          errorMessage: t('subSellerOnboarding.errorMessage')
        })
      );
    }
  };

  const gdsOptions = GDSOptions[i18n.language]
    .slice()
    .sort((a, b) => a.company_name.localeCompare(b.company_name));

  // if (loading) {
  //   return <div>Loading...</div>;
  // }

  // if (error) {
  //   return <div>Error: {error}</div>;
  // }

  const handleRadioKeyDown = (
    e: React.KeyboardEvent,
    currentValue: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    options: string[]
  ) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();

      const currentIndex = options.indexOf(currentValue);
      const prevIndex =
        currentIndex <= 0 ? options.length - 1 : currentIndex - 1;
      const radioButtons = (e.target as HTMLElement)
        .closest('[role="radiogroup"]')
        ?.querySelectorAll('input[type="radio"]');

      if (radioButtons && radioButtons[prevIndex]) {
        (radioButtons[prevIndex] as HTMLInputElement).focus();
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();

      const currentIndex = options.indexOf(currentValue);
      const nextIndex = (currentIndex + 1) % options.length;
      const radioButtons = (e.target as HTMLElement)
        .closest('[role="radiogroup"]')
        ?.querySelectorAll('input[type="radio"]');

      if (radioButtons && radioButtons[nextIndex]) {
        (radioButtons[nextIndex] as HTMLInputElement).focus();
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      const targetValue = (e.target as HTMLInputElement).value;
      setValue(targetValue);
      if (targetValue === 'no') {
        setShowAlertPopup(true);
      }
    }
  };

  React.useEffect(() => {
    if (formSubmitted && headingRef.current) {
      headingRef.current.focus();
    }
  }, [formSubmitted]);

  React.useEffect(() => {
    if (errorAnnounceRef.current) {
      errorAnnounceRef.current.textContent = announceError;
    }
  }, [announceError]);

  React.useEffect(() => {
    //Announce when page loads
    if (announcementRef.current) {
      announcementRef.current.textContent = '';
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent = `${t(
            'subSellerOnboarding.headingAria'
          )} | Air Canada NDC`;
        }
      }, 120);
    }
  }, [t]);

  React.useEffect(() => {
    // Announce when registration form appears
    const announce = t('subSellerOnboarding.formVisible');
    if (showForm && announcementRef.current) {
      const timer = setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent = announce;
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [showForm, t]);

  return (
    <Box sx={{ display: 'flex', mt: '1rem' }}>
      {/* Hidden announcement div */}
      <div
        ref={announcementRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-1000px'
        }}
      />

      <main style={{ width: '100%' }}>
        {announceError && (
          <Box
            ref={errorAnnounceRef}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            sx={{
              position: 'absolute',
              left: '-1000px'
            }}
            // tabIndex={-1}
          >
            {announceError}
          </Box>
        )}

        {showSuccessPopUp && <SuccessPopUp topPx="120px" />}
        {formSubmitted ? (
          <Box
            sx={{
              bgcolor: '#f1f7fa',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '86vh',
              width: '100%'
            }}
            role="status"
            aria-live="assertive"
            aria-atomic="true"
          >
            <Paper
              elevation={3}
              sx={{
                py: '2rem',
                px: isMobile ? '1rem' : '5rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '40rem',
                mx: isMobile ? '1rem' : 0
              }}
            >
              <Typography
                ref={headingRef}
                variant="h5"
                fontWeight={600}
                mb={3}
                textAlign={'center'}
                tabIndex={-1}
              >
                {t('subSellerOnboarding.conclude.title')}
              </Typography>
              <Typography fontSize={'.875rem'}>
                {t('subSellerOnboarding.conclude.subtitle.0')}
              </Typography>

              <Typography fontSize={'.875rem'} textAlign={'center'}>
                {t('subSellerOnboarding.conclude.subtitle.1')}
              </Typography>
            </Paper>
          </Box>
        ) : (
          <Grid container columnGap={7}>
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              lg={4}
              bgcolor="#f1f7fa"
              p={4}
              minHeight={isMobileTablet ? '' : '100vh'}
            >
              <Typography
                variant="h1"
                mb={2}
                fontWeight={600}
                fontSize="1.5rem"
                lineHeight={1.334}
                letterSpacing="0em"
              >
                {t('subSellerOnboarding.sidebar.title')}
              </Typography>
              <Typography
                fontSize={'.875rem'}
                mb={2}
                fontWeight={600}
                color="#d8292f"
              >
                {t('subSellerOnboarding.sidebar.note')}
              </Typography>
              <Typography fontSize={'.875rem'} mb={2}>
                {t('subSellerOnboarding.sidebar.subtitle.0')}
              </Typography>
              <Typography fontSize={'.875rem'}>
                {t('subSellerOnboarding.sidebar.subtitle.1')}
              </Typography>
            </Grid>
            <Grid
              item
              container
              xs={12}
              sm={12}
              md={7}
              lg={7}
              pt={4}
              sx={{ ...(isMobile && { mx: 4 }) }}
            >
              {/* Qualifying Questions */}
              <Grid item xs={12} sm={12} md={12} lg={12} mb={1} mt={1}>
                {/* Question 1 */}
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel
                    component="legend"
                    sx={{
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: '#000',
                      mb: 1,
                      '&.Mui-focused': { color: '#000' }
                    }}
                  >
                    {t('subSellerOnboarding.questions.questionOne')}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={question1Answer}
                    onChange={(e) => {
                      setQuestion1Answer(e.target.value);
                      if (e.target.value === 'no') {
                        setQuestion2Answer('');
                        setShowAlertPopup(true);
                      }
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={
                        <Radio
                          onKeyDown={(e) =>
                            handleRadioKeyDown(
                              e,
                              question1Answer,
                              setQuestion1Answer,
                              ['yes', 'no']
                            )
                          }
                        />
                      }
                      label={t('subSellerOnboarding.questions.answers.yes')}
                      sx={{ mr: 4 }}
                    />
                    <FormControlLabel
                      value="no"
                      control={
                        <Radio
                          onKeyDown={(e) =>
                            handleRadioKeyDown(
                              e,
                              question1Answer,
                              setQuestion1Answer,
                              ['yes', 'no']
                            )
                          }
                        />
                      }
                      label={t('subSellerOnboarding.questions.answers.no')}
                    />
                  </RadioGroup>
                </FormControl>

                {/* Question 2 - Always visible but disabled if Question 1 is not Yes */}
                <Box
                  component="fieldset"
                  {...(question1Answer !== 'yes' && { inert: '' as any })}
                  sx={{
                    border: 'none',
                    m: 0,
                    p: 0
                  }}
                >
                  <FormControl
                    component="fieldset"
                    sx={{ mb: 3, width: '100%' }}
                    disabled={question1Answer !== 'yes'}
                  >
                    <FormLabel
                      component="legend"
                      sx={{
                        fontSize: '1.1rem',
                        fontWeight: 500,
                        color: question1Answer !== 'yes' ? '#9e9e9e' : '#000',
                        mb: 1,
                        '&.Mui-focused': { color: '#000' }
                      }}
                    >
                      {t('subSellerOnboarding.questions.questionTwo')}
                    </FormLabel>
                    <RadioGroup
                      row
                      value={question2Answer}
                      onChange={(e) => {
                        setQuestion2Answer(e.target.value);
                        if (e.target.value === 'no') {
                          setShowAlertPopup(true);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="yes"
                        control={
                          <Radio
                            onKeyDown={(e) =>
                              handleRadioKeyDown(
                                e,
                                question2Answer,
                                setQuestion2Answer,
                                ['yes', 'no']
                              )
                            }
                          />
                        }
                        label={t('subSellerOnboarding.questions.answers.yes')}
                        sx={{
                          mr: 4,
                          '& .MuiFormControlLabel-label': {
                            color:
                              question1Answer !== 'yes' ? '#9e9e9e' : 'inherit'
                          }
                        }}
                      />
                      <FormControlLabel
                        value="no"
                        control={
                          <Radio
                            onKeyDown={(e) =>
                              handleRadioKeyDown(
                                e,
                                question2Answer,
                                setQuestion2Answer,
                                ['yes', 'no']
                              )
                            }
                          />
                        }
                        label={t('subSellerOnboarding.questions.answers.no')}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            color:
                              question1Answer !== 'yes' ? '#9e9e9e' : 'inherit'
                          }
                        }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Show message if not eligible */}
                {(question1Answer === 'no' ||
                  (question1Answer === 'yes' && question2Answer === 'no')) && (
                  <AlertPopUp
                    open={showAlertPopup}
                    handleClose={() => setShowAlertPopup(false)}
                    header={t('subSellerOnboarding.popUp.title')}
                    ariaLabel={t('subSellerOnboarding.popUp.aria-label')}
                    body={t('subSellerOnboarding.popUp.description')}
                    buttonContent={t('subSellerOnboarding.popUp.button')}
                    language={i18n.language}
                  />
                )}
              </Grid>

              {/* Form - Only show if both questions are answered Yes */}
              {showForm && (
                <>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    mb={isMobile ? 5 : 27}
                  >
                    <Typography fontWeight={600} fontSize={'20px'} gutterBottom>
                      {t('subSellerOnboarding.form.title')}
                    </Typography>
                    <div style={{ marginTop: 16, marginBottom: 8 }}>
                      <CustomDropdown
                        label={t('subSellerOnboarding.form.field1.data')}
                        options={gdsOptions.map((item: any) => ({
                          label: item.company_name,
                          value: item.seller_aggregator_id
                        }))}
                        value={formData.seller_aggregator_id}
                        onChange={val => {
                          handleFormData({
                            target: {
                              name: 'seller_aggregator_id',
                              value: val,
                              id: 'seller_aggregator_id',
                            }
                          });
                        }}
                        placeholder={t('subSellerOnboarding.form.ariaLabel')}
                        disabled={false}
                        required ={true}
                      />
                      {formError.seller_aggregator_id && <FormHelperText error>{formError.seller_aggregator_id}</FormHelperText>}
                    </div>

                    <TextField
                      variant="standard"
                      id="text"
                      name="travel_agency_name"
                      value={formData.travel_agency_name}
                      onChange={handleFormData}
                      error={Boolean(formError.travel_agency_name)}
                      helperText={formError.travel_agency_name}
                      InputLabelProps={{
                        // htmlFor:"travel_agency_name",
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required
                      size="small"
                      label={t('subSellerOnboarding.form.field2.data')}
                      inputProps={{
                        'aria-label': t('subSellerOnboarding.form.field2.data'),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                    />

                    {/* <Typography
                      fontWeight={600}
                      fontSize="20px"
                      mt={5}
                      gutterBottom
                    >
                      {t('subSellerOnboarding.form.field9.data')}
                    </Typography> */}

                  <div style={{ marginTop: 16, marginBottom: 8 }}>
                    <CustomDropdown
                      label={t('subSellerOnboarding.form.field9.data')}
                      options={businessModelOptions[i18n.language].map((item: any) => ({
                        label: item.business_type,
                        value: item.sub_seller_business_type_id
                      }))}
                      value={formData.sub_seller_business_type_id}
                      onChange={val => {
                        handleFormData({
                          target: {
                            name: 'sub_seller_business_type_id',
                            value: val,
                            id: 'sub_seller_business_type_id',
                          }
                        });
                      }}
                      placeholder={t('subSellerOnboarding.form.field9.data')}
                      disabled={false}
                      required ={true}
                    />
                    {formError.sub_seller_business_type_id && <FormHelperText error>{formError.sub_seller_business_type_id}</FormHelperText>}
                  </div>

                    <TextField
                      variant="standard"
                      id="iata"
                      name="iata_number"
                      value={formData.iata_number}
                      onChange={handleFormData}
                      error={Boolean(formError.iata_number)}
                      helperText={
                        formError.iata_number ||
                        t('subSellerOnboarding.form.field3.helperText')
                      }
                      InputLabelProps={{
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required={isIataRequired}
                      size="small"
                      label={t('subSellerOnboarding.form.field3.data')}
                      inputProps={{
                        'aria-label': t('subSellerOnboarding.form.field3.data'),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                    />

                    <TextField
                      variant="standard"
                      id="text"
                      name="agency_address"
                      value={formData.agency_address}
                      onChange={handleFormData}
                      error={Boolean(formError.agency_address)}
                      helperText={formError.agency_address}
                      InputLabelProps={{
                        // htmlFor: "agency_address",
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required
                      size="small"
                      label={t('subSellerOnboarding.form.field4.data')}
                      inputProps={{
                        'aria-label': t('subSellerOnboarding.form.field4.data'),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                    />

                    <TextField
                      variant="standard"
                      id="phone"
                      name="agency_phone_number"
                      value={formData.agency_phone_number}
                      onChange={handleFormData}
                      error={Boolean(formError.agency_phone_number)}
                      helperText={formError.agency_phone_number}
                      InputLabelProps={{
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required
                      size="small"
                      label={t('subSellerOnboarding.form.field5.data')}
                      inputProps={{
                        'aria-label': t('subSellerOnboarding.form.field5.data'),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                    />

                    <TextField
                      variant="standard"
                      id="text"
                      name="contact_first_name"
                      value={formData.contact_first_name}
                      onChange={handleFormData}
                      error={Boolean(formError.contact_first_name)}
                      helperText={formError.contact_first_name}
                      InputLabelProps={{
                        // htmlFor: "contact_first_name",
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required
                      size="small"
                      label={t('subSellerOnboarding.form.field10.data')}
                      inputProps={{
                        'aria-label': t(
                          'subSellerOnboarding.form.field10.data'
                        ),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                    />

                    <TextField
                      variant="standard"
                      id="text"
                      name="contact_last_name"
                      value={formData.contact_last_name}
                      onChange={handleFormData}
                      error={Boolean(formError.contact_last_name)}
                      helperText={formError.contact_last_name}
                      InputLabelProps={{
                        // htmlFor: "contact_last_name",
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required
                      size="small"
                      label={t('subSellerOnboarding.form.field11.data')}
                      inputProps={{
                        'aria-label': t(
                          'subSellerOnboarding.form.field11.data'
                        ),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                    />
                    <TextField
                      variant="standard"
                      id="email"
                      name="agency_email"
                      value={formData.agency_email}
                      onChange={handleFormData}
                      error={Boolean(formError.agency_email)}
                      helperText={
                        formError.agency_email ||
                        t('subSellerOnboarding.form.field6.helperText')
                      }
                      inputProps={{
                        'aria-label': t('subSellerOnboarding.form.field6.data'),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                      InputLabelProps={{
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required
                      size="small"
                      label={t('subSellerOnboarding.form.field6.data')}
                    />
                    <TextField
                      variant="standard"
                      id="url"
                      name="agency_website"
                      value={formData.agency_website}
                      onChange={handleFormData}
                      error={Boolean(formError.agency_website)}
                      helperText={formError.agency_website}
                      InputLabelProps={{
                        style: { fontSize: '1rem' },
                        sx: {
                          color: '#000',
                          '&.Mui-focused, &.MuiFormLabel-filled': {
                            color: '#C1C5C8'
                          }
                        }
                      }}
                      sx={{ mt: 2.5, width: '100%' }}
                      required
                      size="small"
                      label={t('subSellerOnboarding.form.field7.data')}
                      inputProps={{
                        'aria-label': t('subSellerOnboarding.form.field7.data'),
                        style: {
                          padding: '.4rem 0'
                        }
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    mb={isMobile ? 7 : 15}
                    alignItems={'end'}
                    justifyContent={'center'}
                  >
                    <Button onClick={handleSubmit} variant="primary" size="md">
                      {t('subSellerOnboarding.form.submit')}
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </main>
    </Box>
  );
};

export default SignUp;